import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { WorkspaceProvider, useFileSystem, useSync, useWorkspaceSwitch, useWorkspaceMetadata, useWorkspace, useIdentity, useRegistry } from './hooks/useWorkspace'
import { useTheme } from './hooks/useTheme'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { usePwaInstall } from './hooks/usePwaInstall'
import { reconcilePushSubscriptions, unsubscribeBrowserPush, type PushBlobSubscription } from './engine/pushSubscription'
import { ToastProvider, useToast } from './components/Toast'
import { detectFileExtension } from './utils/detectFileExtension'
import { imageExtensionFromMimeType } from './utils/imageExtension'
import { htmlToMarkdown, hasRichHtmlContent } from './utils/htmlToMarkdown'
import { cascadeRename } from './utils/cascadeFileOps'
import TopBar from './components/TopBar'
import FileBrowser from './components/FileBrowser'
import Tabs from './components/Tabs'
import EditorView from './components/EditorView'
import QuickOpen from './components/QuickOpen'
import SearchPanel from './components/SearchPanel'
import WorkspacePicker from './components/WorkspacePicker'
import ProfileSettings from './components/ProfileSettings'
import UnlockPrompt from './components/UnlockPrompt'
import ShareModal from './components/ShareModal'
import HistoryPanel from './components/HistoryPanel'
import SnapshotBrowserPanel from './components/SnapshotBrowserPanel'
import GuestBanner from './components/GuestBanner'
import BetaBanner from './components/BetaBanner'
import WorkspaceSettings, { WorkspaceDeletedOverlay } from './components/WorkspaceSettings'
import InstallBanner from './components/InstallBanner'
import ContextMenu from './components/ContextMenu'
import { X, Copy, FolderOpen, Layers, ArrowRightToLine, XCircle, Pencil } from 'lucide-react'
import './App.css'
import McpAuthorizePage from './components/Mcp/McpAuthorizePage'
import DebugPage from './components/Debug/DebugPage'
import AdminPage from './components/Admin/AdminPage'
import ChangesPage from './components/ChangesPage'
import { EmbeddedAgentProvider } from './contexts/EmbeddedAgentContext'
import { AgentMiniWidget } from './components/AgentMiniWidget'
import ComponentJobMiniWidget from './components/ComponentJobMiniWidget'
import { useWorkspaceTabs } from './hooks/useWorkspaceTabs'
import { createWorkspaceAuthProvider } from '@secureblob/sdk/workspaceAuth'
import { parseWorkspaceFromHash } from './engine/parseUrl'
import { APP_BASE_PATH, SIGNUP_ENABLED } from './config'
import { DEFAULT_AGENT_CONFIG_JSON } from './engine/defaultAgentConfig'
import { getDedicatedAppPage } from './utils/appRoute'

import type { CommitSummary, FsExternalChangeEvent, EngineManagerEventPayload, DiffState } from './engine/types'
import { useEngineManager } from './hooks/useWorkspace'

/** Extract the blobId (first segment) from a URL hash string. */
function getBlobIdFromHash(hash: string): string {
    const trimmed = hash.startsWith('#') ? hash.slice(1) : hash
    if (!trimmed || trimmed === 'demo') return trimmed
    return parseWorkspaceFromHash(hash).entry?.blobId ?? trimmed.split('&')[0]
}

function getInitialPathFromHash(): string {
    return parseWorkspaceFromHash(window.location.hash).initialPath ?? '/welcome.md'
}

function getInitialViewModeFromHash(): 'edit' | 'preview' | null {
    return parseWorkspaceFromHash(window.location.hash).initialViewMode
}

function isAgentFilePath(path: string | null): boolean {
    if (path == null) return false
    return /\.agent\.json$/i.test(path) || /\.agent\.json\.transcripts\//.test(path)
}


// ── Sidebar resize constants ────────────────────────────────────
const SIDEBAR_DEFAULT = 260
const SIDEBAR_MIN = 180
const SIDEBAR_MAX = 500

function AppShell() {
    const { theme, toggleTheme } = useTheme()
    const online = useOnlineStatus()
    const toast = useToast()
    const fileSystem = useFileSystem()
    const { createFile, renameFile, files, loadedDirs } = fileSystem
    const { syncNow, syncing } = useSync()
    const { activeBlobId, activateWorkspace } = useWorkspaceSwitch()
    const { hasIdentity, identity, unlockWithWebAuthn, createIdentity, vaultExists, vaultUnlockMethod, isGuest, recoverFromServer } = useIdentity()
    const { entries } = useRegistry()
    const { isStandalone } = usePwaInstall()
    const { canWrite, label: wsLabel } = useWorkspaceMetadata()
    const workspace = useWorkspace()
    const manager = useEngineManager()
    const prevOnline = useRef(online)

    // Tab state — per-workspace persistence via useWorkspaceTabs
    const knownPaths = useMemo(() => new Set(files.map(f => f.path)), [files])
    const filePaths = useMemo(() => new Set(files.filter(f => f.kind !== 'directory').map(f => f.path)), [files])
    const loadedDirPaths = useMemo(() => new Set(loadedDirs), [loadedDirs])
    const isPathInLoadedScope = useCallback((path: string) => {
        const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
        const idx = normalized.lastIndexOf('/')
        const parent = idx <= 0 ? '/' : normalized.slice(0, idx)
        return loadedDirPaths.has(parent)
    }, [loadedDirPaths])
    const fileExists = useMemo(() => {
        const fs = workspace?.fs
        return (path: string) => knownPaths.has(path) || !!fs?.stat(path) || !isPathInLoadedScope(path)
    }, [workspace, knownPaths, isPathInLoadedScope])
    // Reactive hash state to trigger updates on paste/navigation without reload
    const [hash, setHash] = useState(window.location.hash)
    useEffect(() => {
        const handle = () => setHash(window.location.hash)
        window.addEventListener('hashchange', handle)
        return () => window.removeEventListener('hashchange', handle)
    }, [])

    // initialPath and isShareLinkEntry: derived from sessionStorage (bridge) or URL.
    // They are reactive to workspace changes (activeBlobId) and hash changes.
    const initialPath = useMemo(() => {
        const fromSession = sessionStorage.getItem('sb:initial-path')
        if (fromSession) {
            return fromSession
        }

        // Race-condition guard: hash may point to a workspace that hasn't activated yet.
        // Defer until activeBlobId catches up — this memo re-runs when it does.
        const hashBlobId = getBlobIdFromHash(hash)
        if (activeBlobId && hashBlobId && hashBlobId !== activeBlobId && hashBlobId !== 'demo') {
            return null
        }

        return getInitialPathFromHash()
    }, [activeBlobId, hash])

    const isShareLinkEntry = useMemo(() => {
        const parsed = parseWorkspaceFromHash(hash)
        return !!sessionStorage.getItem('sb:guest-active') || !!parsed.entry
    }, [hash])

    const { openPaths, activePath, openFile, closeTab: hookCloseTab, closeOtherTabs, closeTabsToRight, closeAllTabs, reorderTabs, renameTabPaths, removeTabPaths } = useWorkspaceTabs(activeBlobId, fileExists, knownPaths, initialPath, isPathInLoadedScope, filePaths)
    const [recentPaths, setRecentPaths] = useState<string[]>(() => initialPath ? [initialPath] : [])
    const [externallyModifiedPaths, setExternallyModifiedPaths] = useState<string[]>([])

    // Sync initialPath into recentPaths whenever it changes reactively
    useEffect(() => {
        if (initialPath) setRecentPaths(prev => prev.includes(initialPath) ? prev : [...prev, initialPath])
    }, [initialPath])

    const [initialViewMode, setInitialViewMode] = useState<'edit' | 'preview' | null>(null)
    useEffect(() => {
        const fromSession = sessionStorage.getItem('sb:initial-view-mode')
        if (fromSession === 'edit' || fromSession === 'preview') {
            setInitialViewMode(fromSession)
        } else {
            setInitialViewMode(getInitialViewModeFromHash())
        }
    }, [activeBlobId, hash])
    const [currentViewMode, setCurrentViewMode] = useState<'edit' | 'preview' | 'agent' | 'job'>('edit')
    const [requestedEditorMode, setRequestedEditorMode] = useState<{ path: string; mode: 'job' } | null>(null)
    const [scrollToLine, setScrollToLine] = useState<number | null>(null)
    const handleScrollToLineApplied = useCallback(() => setScrollToLine(null), [])
    const handleViewModeChange = useCallback((m: 'edit' | 'preview' | 'agent' | 'job') => {
        setCurrentViewMode(m)
        setRequestedEditorMode(current => {
            if (!current || current.mode !== m || current.path !== activePath) {
                return current
            }
            return null
        })
        // Clear initialViewMode after first consumption so it doesn't re-apply on file switch
        setInitialViewMode(null)
    }, [activePath])
    const suppressSyncToastsInChat = currentViewMode === 'agent' && isAgentFilePath(activePath)

    useEffect(() => {
        const pendingInitialPath = sessionStorage.getItem('sb:initial-path')
        if (pendingInitialPath && activePath === pendingInitialPath) {
            sessionStorage.removeItem('sb:initial-path')
        }
    }, [activePath])

    useEffect(() => {
        const pendingInitialViewMode = sessionStorage.getItem('sb:initial-view-mode')
        if ((pendingInitialViewMode === 'edit' || pendingInitialViewMode === 'preview') && currentViewMode === pendingInitialViewMode) {
            sessionStorage.removeItem('sb:initial-view-mode')
        }
    }, [currentViewMode])

    // URL Cleanup: Clear the hash if the user navigates away from the shared file or workspace.
    // This removes the capability key (security) from the address bar when it's no longer the focus.
    useEffect(() => {
        if (!hash) return

        // Race-condition guard: if the hash targets a different workspace whose
        // activation is still in flight, do NOT clear — WorkspaceProvider needs
        // the hash intact to complete the switch.
        const hashBlobId = getBlobIdFromHash(hash)
        if (activeBlobId && hashBlobId !== activeBlobId) {
            return
        }

        // Different file: clear hash when the user navigates away from the shared file.
        // Compare against both activePath and initialPath to avoid clearing mid-navigation.
        const hashSegment = hash.startsWith('#') ? hash.slice(1) : hash
        const urlParams = new URLSearchParams(hashSegment.split('&').slice(1).join('&'))
        const urlRawPath = urlParams.get('path')
        if (urlRawPath) {
            const urlPath = `/${urlRawPath.replace(/^\/+/, '')}`
            if (activePath && activePath !== urlPath && initialPath !== urlPath) {
                history.replaceState(null, '', location.pathname + location.search)
            }
        }
    }, [activeBlobId, activePath, initialPath, hash])

    // Modals
    const [showQuickOpen, setShowQuickOpen] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [showPicker, setShowPicker] = useState(false)
    const [showProfile, setShowProfile] = useState(false)
    const [profileInitialTab, setProfileInitialTab] = useState<'mcp' | 'profile' | 'identity' | 'notifications' | 'export' | undefined>(undefined)
    const [showShare, setShowShare] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [historyPanelState, setHistoryPanelState] = useState<{ initialMode: 'all' | 'file'; filePath: string | null }>({
        initialMode: 'all',
        filePath: null,
    })
    const [snapshotCommit, setSnapshotCommit] = useState<CommitSummary | null>(null)
    const [showSettings, setShowSettings] = useState(false)
    const [deletedOverlay, setDeletedOverlay] = useState<{ label: string; deletedByName?: string } | null>(null)
    const [showUnlockPrompt, setShowUnlockPrompt] = useState(false)
    const [showPwaSignIn, setShowPwaSignIn] = useState(false)
    // Sticky dismiss: once user clicks "Continue as guest", don't re-show
    // the overlay until next tab session (Identity Spec §4.6 #4).
    const [pwaSignInDismissed, setPwaSignInDismissed] = useState(
        () => sessionStorage.getItem('sb:pwa-sign-in-dismissed') === '1'
    )

    useEffect(() => {
        if (!hasIdentity || !online) return

        const pushEnabledEntries = entries.filter(entry => entry.notifications?.push === true)
        if (pushEnabledEntries.length === 0) return

        let cancelled = false

        void (async () => {
            const subscriptionsByApiBase = new Map<string, PushBlobSubscription[]>()
            for (const entry of pushEnabledEntries) {
                const authProvider = createWorkspaceAuthProvider(entry.apiBaseUrl, entry.capability, entry.blobId)
                if (!authProvider) continue

                const existing = subscriptionsByApiBase.get(entry.apiBaseUrl) ?? []
                existing.push({ blobId: entry.blobId, authProvider })
                subscriptionsByApiBase.set(entry.apiBaseUrl, existing)
            }

            for (const [apiBaseUrl, subscriptions] of subscriptionsByApiBase) {
                try {
                    await reconcilePushSubscriptions(apiBaseUrl, subscriptions)
                } catch (error) {
                    if (!cancelled) {
                        console.warn(`Failed to reconcile push subscriptions for ${apiBaseUrl}:`, error)
                    }
                }
            }

            if (!cancelled && typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                await unsubscribeBrowserPush().catch(error => {
                    if (!cancelled) {
                        console.warn('Failed to unsubscribe browser push after permission denial:', error)
                    }
                })
            }
        })()

        return () => {
            cancelled = true
        }
    }, [entries, hasIdentity, online])


    // Listen for workspace:deleted event (tombstone received via sync)
    useEffect(() => {
        const unsub = manager.on('workspace:deleted', (ev: EngineManagerEventPayload['workspace:deleted']) => {
            setDeletedOverlay({ label: wsLabel, deletedByName: ev.deletedByKid })
        })
        return unsub
    }, [manager, wsLabel])

    // Show unlock prompt when WebAuthn vault exists but identity is not loaded.
    // Skip for share-link entries — content should be visible immediately (§1.3 ReadWorkspace).
    // User can still unlock via the profile button or GuestBanner at any time.
    useEffect(() => {
        if (isShareLinkEntry) return
        if (!hasIdentity && vaultExists && vaultUnlockMethod?.startsWith('webauthn')) {
            setShowUnlockPrompt(true)
        } else {
            setShowUnlockPrompt(false)
        }
    }, [hasIdentity, vaultExists, vaultUnlockMethod, isShareLinkEntry])

    // Show sign-in prompt when no local vault + no identity (all platforms).
    // Note: vaultExists starts as null (async check) — use === false to avoid flash.
    // Skip for share-link entries — zero-click read, GuestBanner replaces blocking modal (§1.3).
    useEffect(() => {
        if (pwaSignInDismissed) return
        if (isShareLinkEntry) return
        if (!hasIdentity && vaultExists === false) {
            setShowPwaSignIn(true)
        } else {
            setShowPwaSignIn(false)
        }
    }, [hasIdentity, vaultExists, pwaSignInDismissed, isShareLinkEntry])

    // Reset dismiss state when identity is acquired (so overlay returns on next fresh visit)
    useEffect(() => {
        if (hasIdentity) {
            sessionStorage.removeItem('sb:pwa-sign-in-dismissed')
            setPwaSignInDismissed(false)
        }
    }, [hasIdentity])

    // Profile button: try WebAuthn unlock if vault is locked, then open modal
    const handleOpenProfile = useCallback(async () => {
        if (!hasIdentity && vaultExists && vaultUnlockMethod?.startsWith('webauthn')) {
            try {
                const ok = await unlockWithWebAuthn()
                if (ok) return // Successful unlock → workspace transition, don't show profile
            } catch { /* user cancelled or error — fall through to open modal */ }
        }
        setShowProfile(true)
    }, [hasIdentity, vaultExists, vaultUnlockMethod, unlockWithWebAuthn])

    // Diff view state: null = normal, { path, commitHash, commitMeta } = diff mode
    const [diffState, setDiffState] = useState<DiffState | null>(null)

    // Sidebar state: mobile = drawer, desktop = collapsible.
    // Auto-collapse file explorer for read-only share links (immersive reading).
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => isShareLinkEntry && !canWrite
    )

    // Resizable sidebar (desktop only)
    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem('sb:sidebar-width')
        return saved ? Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, Number(saved))) : SIDEBAR_DEFAULT
    })
    const [isResizing, setIsResizing] = useState(false)
    const resizing = useRef(false)
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        resizing.current = true
        setIsResizing(true)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        const onMove = (ev: MouseEvent) => {
            if (!resizing.current) return
            const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, ev.clientX))
            setSidebarWidth(w)
        }
        const onUp = () => {
            resizing.current = false
            setIsResizing(false)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            // Persist final width (not on every mousemove — avoids sync I/O jank)
            setSidebarWidth(w => { localStorage.setItem('sb:sidebar-width', String(Math.round(w))); return w })
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }, [])
    const handleResizeDoubleClick = useCallback(() => {
        setSidebarWidth(SIDEBAR_DEFAULT)
        localStorage.setItem('sb:sidebar-width', String(SIDEBAR_DEFAULT))
    }, [])

    const [switchTransition, setSwitchTransition] = useState(false)
    const [loadingPaths, setLoadingPaths] = useState<string[]>([])
    const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const previousWorkspace = useRef(activeBlobId)

    // Track recent files
    const trackRecent = useCallback((path: string) => {
        setRecentPaths(prev => {
            const filtered = prev.filter(p => p !== path)
            return [path, ...filtered].slice(0, 10)
        })
    }, [])

    // Toast on identity auto-import from hash URL
    useEffect(() => {
        const handler = () => toast('Profile imported successfully ✓', { type: 'success' })
        window.addEventListener('sb:identity-imported', handler)
        return () => window.removeEventListener('sb:identity-imported', handler)
    }, [toast])

    // Listen for 'open workspace settings' custom event (from WorkspacePicker context menu)
    useEffect(() => {
        const handler = (event: Event) => {
            void (async () => {
                const targetBlobId = (event as CustomEvent<{ blobId?: string }>).detail?.blobId
                try {
                    if (targetBlobId && targetBlobId !== activeBlobId) {
                        await activateWorkspace(targetBlobId)
                    }
                    setShowSettings(true)
                } catch (err) {
                    toast(
                        err instanceof Error
                            ? `Could not open workspace settings: ${err.message}`
                            : 'Could not open workspace settings',
                        { type: 'error' },
                    )
                }
            })()
        }
        window.addEventListener('sb:open-workspace-settings', handler)
        return () => window.removeEventListener('sb:open-workspace-settings', handler)
    }, [activeBlobId, activateWorkspace, toast])

    const handleSelectFile = useCallback((path: string | null, line?: number) => {
        if (!path) return
        openFile(path)
        setExternallyModifiedPaths(prev => prev.filter(p => p !== path))
        trackRecent(path)
        setDrawerOpen(false)
        if (line != null && line > 0) {
            setScrollToLine(line)
        } else {
            setScrollToLine(null) // Clear stale scrollToLine from previous search result
        }
    }, [openFile, trackRecent])

    const handleOpenComponentJob = useCallback((path: string) => {
        handleSelectFile(path)
        setRequestedEditorMode({ path, mode: 'job' })
    }, [handleSelectFile])

    const handleDiffFile = useCallback((path: string, commit: CommitSummary) => {
        // Open the file tab and enter diff mode
        handleSelectFile(path)
        setDiffState({
            path,
            commitHash: commit.commitHash,
            commitMeta: {
                writerName: commit.writerName ?? 'Unknown',
                writerEmoji: commit.writerEmoji ?? '👤',
                timestamp: commit.time ?? 0,
                id: commit.commitHash,
            },
        })
    }, [handleSelectFile])

    const handleOpenWorkspaceHistory = useCallback(() => {
        setHistoryPanelState({
            initialMode: 'all',
            filePath: activePath,
        })
        setShowHistory(true)
    }, [activePath])

    const handleOpenFileHistory = useCallback((path: string) => {
        setHistoryPanelState({
            initialMode: 'file',
            filePath: path,
        })
        setShowHistory(true)
    }, [])

    const handleBrowseSnapshot = useCallback((commit: CommitSummary) => {
        setSnapshotCommit(commit)
        setShowHistory(false)
    }, [])

    const handleExitDiff = useCallback(() => {
        setDiffState(null)
    }, [])

    const handleCloseTab = useCallback((path: string) => {
        hookCloseTab(path)
    }, [hookCloseTab])

    const handleLoadingChange = useCallback((filePath: string, isLoading: boolean) => {
        setLoadingPaths(prev => {
            if (isLoading && !prev.includes(filePath)) return [...prev, filePath]
            if (!isLoading && prev.includes(filePath)) return prev.filter(p => p !== filePath)
            return prev
        })
    }, [])

    const handleReorderTabs = useCallback((fromIndex: number, toIndex: number) => {
        reorderTabs(fromIndex, toIndex)
    }, [reorderTabs])

    // Tab context menu state
    const [tabContextMenu, setTabContextMenu] = useState<{ path: string; x: number; y: number } | null>(null)
    const [tabRenamingPath, setTabRenamingPath] = useState<string | null>(null)

    const handleTabContextMenu = useCallback((path: string, x: number, y: number) => {
        setTabContextMenu({ path, x, y })
    }, [])

    const handleTabContextAction = useCallback((action: string) => {
        if (!tabContextMenu) return
        const { path } = tabContextMenu
        switch (action) {
            case 'close':
                hookCloseTab(path)
                break
            case 'closeOthers':
                closeOtherTabs(path)
                break
            case 'closeToRight':
                closeTabsToRight(path)
                break
            case 'closeAll':
                closeAllTabs()
                break
            case 'copyPath':
                navigator.clipboard.writeText(path).then(() => {
                    toast('Path copied', { type: 'success', duration: 1500 })
                }).catch(() => { })
                break
            case 'revealInSidebar':
                handleSelectFile(path)
                setSidebarCollapsed(false)
                break
            case 'rename':
                // Trigger inline rename in Tabs via renamingPath prop
                setTabRenamingPath(path)
                // Reset after a tick so it can re-trigger on same path
                setTimeout(() => setTabRenamingPath(null), 100)
                break
        }
        setTabContextMenu(null)
    }, [tabContextMenu, hookCloseTab, closeOtherTabs, closeTabsToRight, closeAllTabs, toast, handleSelectFile])

    // Online/offline toasts
    useEffect(() => {
        if (prevOnline.current && !online) {
            toast('You are offline', { type: 'offline', duration: 5000 })
        }
        if (!prevOnline.current && online) {
            toast('Back online — syncing…', { type: 'success' })
        }
        prevOnline.current = online
    }, [online, toast])

    // Global keyboard shortcuts
    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            const mod = e.metaKey || e.ctrlKey

            if (mod && e.key === 'p') {
                e.preventDefault()
                setShowQuickOpen(true)
                setShowSearch(false)
            }
            if (mod && e.shiftKey && e.key === 'f') {
                e.preventDefault()
                setShowSearch(true)
                setShowQuickOpen(false)
                setShowPicker(false)
            }
            if (mod && e.key === 'k') {
                e.preventDefault()
                setShowPicker(prev => !prev)
                setShowQuickOpen(false)
                setShowSearch(false)
            }
            // Ctrl/Cmd + , → open workspace settings
            if (mod && e.key === ',') {
                e.preventDefault()
                setShowSettings(true)
            }

            if (mod && e.key === 's') {
                e.preventDefault()
                if (!syncing) {
                    void syncNow().catch(() => {
                        toast('Sync failed — please retry', { type: 'error' })
                    })
                }
            }
            // Control+Shift+A → open default agent panel
            if (mod && e.shiftKey && e.key === 'A') {
                e.preventDefault()
                const files = workspace.fs.list()
                const agentPath = files.find(f => f.path === '/.agent.json')?.path
                    ?? files.find(f => f.path === '/assistant.agent.json')?.path
                    ?? files.find(f => f.path.endsWith('.agent.json'))?.path
                if (agentPath) {
                    handleSelectFile(agentPath)
                } else {
                    workspace.fs.writeText('/assistant.agent.json', DEFAULT_AGENT_CONFIG_JSON)
                    handleSelectFile('/assistant.agent.json')
                }
            }
        }

        async function handlePasteEvent(e: ClipboardEvent) {
            const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase()
            if (tag === 'input' || tag === 'textarea' || (document.activeElement as HTMLElement)?.isContentEditable) return

            const createFromText = (text: string | null) => {
                if (!text?.trim()) return false
                if (!canWrite) { toast('Workspace is read-only', { type: 'error' }); return true }
                const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, '')
                const ext = detectFileExtension(text)
                const path = `/paste-${ts}${ext}`
                createFile(path, text)
                handleSelectFile(path)
                toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                return true
            }

            const createFromImageBlob = async (blob: Blob, mimeType: string) => {
                if (!canWrite) { toast('Workspace is read-only', { type: 'error' }); return true }
                const ext = imageExtensionFromMimeType(mimeType)
                const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, '')
                const path = `/images/paste-${ts}.${ext}`
                const buffer = await blob.arrayBuffer()
                workspace.fs.writeBytes(path, new Uint8Array(buffer))
                handleSelectFile(path)
                toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                return true
            }

            const items = e.clipboardData?.items
            if (items && items.length) {
                // Try text/html first for rich content (Excel tables, Word formatting)
                for (const item of Array.from(items)) {
                    if (item.type === 'text/html') {
                        const html = await new Promise<string>((resolve) => item.getAsString(resolve))
                        if (hasRichHtmlContent(html)) {
                            const md = htmlToMarkdown(html)
                            if (md.trim()) {
                                if (!canWrite) { toast('Workspace is read-only', { type: 'error' }); e.preventDefault(); return }
                                const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, '')
                                const path = `/paste-${ts}.md`
                                createFile(path, md)
                                handleSelectFile(path)
                                toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                                e.preventDefault()
                                return
                            }
                        }
                    }
                }
                for (const item of Array.from(items)) {
                    if (item.type.startsWith('image/')) {
                        const file = item.getAsFile()
                        if (!file) continue
                        if (await createFromImageBlob(file, item.type)) {
                            e.preventDefault()
                            return
                        }
                    }
                    if (item.type === 'text/plain') {
                        const text = await new Promise<string>((resolve) => item.getAsString(resolve))
                        if (createFromText(text)) {
                            e.preventDefault()
                            return
                        }
                    }
                }
            }

            // Fallback for environments where clipboardData is unavailable/empty
            try {
                const clipboardItems = await navigator.clipboard.read()
                for (const item of clipboardItems) {
                    const imageType = item.types.find(t => t.startsWith('image/'))
                    if (imageType) {
                        const blob = await item.getType(imageType)
                        if (await createFromImageBlob(blob, imageType)) {
                            e.preventDefault()
                            return
                        }
                    }
                    if (item.types.includes('text/plain')) {
                        const blob = await item.getType('text/plain')
                        const text = await blob.text()
                        if (createFromText(text)) {
                            e.preventDefault()
                            return
                        }
                    }
                }
            } catch {
                // Clipboard read unavailable; default paste behavior continues
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('paste', handlePasteEvent)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('paste', handlePasteEvent)
        }
    }, [canWrite, createFile, workspace, handleSelectFile, syncing, syncNow, toast])

    // Workspace switch transition animation (tab save/restore handled by useWorkspaceTabs)
    useEffect(() => {
        if (!activeBlobId || previousWorkspace.current === activeBlobId) return

        previousWorkspace.current = activeBlobId
        setSwitchTransition(true)

        if (transitionTimer.current) clearTimeout(transitionTimer.current)
        transitionTimer.current = setTimeout(() => setSwitchTransition(false), 300)
    }, [activeBlobId])

    useEffect(() => () => {
        if (transitionTimer.current) clearTimeout(transitionTimer.current)
    }, [])


    const pendingExtPaths = useRef<Set<string>>(new Set())
    const extToastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    useEffect(() => {
        return workspace.on('fs:external-change', (ev: FsExternalChangeEvent) => {
            // Tab badge indicator
            setExternallyModifiedPaths(prev => {
                const next = new Set(prev)
                for (const p of ev.paths || []) next.add(p)
                return Array.from(next)
            })

            // Batched sync toast (debounce 500ms to coalesce multi-file updates)
            for (const p of ev.paths || []) pendingExtPaths.current.add(p)
            clearTimeout(extToastTimer.current)
            extToastTimer.current = setTimeout(() => {
                const paths = Array.from(pendingExtPaths.current)
                pendingExtPaths.current.clear()
                if (paths.length === 0) return
                if (suppressSyncToastsInChat) return
                const msg = paths.length === 1
                    ? `${paths[0].split('/').pop()} updated`
                    : `${paths.length} files updated`
                toast(msg, { type: 'sync', duration: 2500 })
            }, 500)
        })
    }, [workspace, toast, suppressSyncToastsInChat])

    // Paste from TopBar button — uses Clipboard API to read content
    const handleTopBarPaste = useCallback(async () => {
        if (!canWrite) { toast('Workspace is read-only', { type: 'error' }); return }
        try {
            if (!navigator.clipboard?.read) {
                toast('Clipboard access not available', { type: 'error' }); return
            }
            const items = await navigator.clipboard.read()
            const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, '')

            for (const item of items) {
                const imageType = item.types.find((t: string) => t.startsWith('image/'))
                if (imageType) {
                    const blob = await item.getType(imageType)
                    const ext = imageType.split('/')[1] === 'png' ? 'png' : 'jpg'
                    const path = `/images/paste-${ts}.${ext}`
                    const buffer = await blob.arrayBuffer()
                    workspace.fs.writeBytes(path, new Uint8Array(buffer))
                    handleSelectFile(path)
                    toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                    return
                }
                // Prefer text/html for rich content (Excel tables, Word formatting)
                if (item.types.includes('text/html')) {
                    try {
                        const blob = await item.getType('text/html')
                        const html = await blob.text()
                        if (hasRichHtmlContent(html)) {
                            const md = htmlToMarkdown(html)
                            if (md.trim()) {
                                const path = `/paste-${ts}.md`
                                createFile(path, md)
                                handleSelectFile(path)
                                toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                                return
                            }
                        }
                    } catch { /* fall through to text/plain */ }
                }
                if (item.types.includes('text/plain')) {
                    const blob = await item.getType('text/plain')
                    const text = await blob.text()
                    if (!text.trim()) continue
                    const ext = detectFileExtension(text)
                    const path = `/paste-${ts}${ext}`
                    createFile(path, text)
                    handleSelectFile(path)
                    toast(`Created ${path.split('/').pop()}`, { type: 'success' })
                    return
                }
            }
            toast('Nothing to paste', { type: 'info' })
        } catch {
            toast('Could not read clipboard', { type: 'error' })
        }
    }, [canWrite, workspace, createFile, handleSelectFile, toast])

    return (
        <EmbeddedAgentProvider workspace={workspace}>
            <div className="app-shell">
                <BetaBanner />

                {!online && (
                    <div className="offline-banner">
                        Offline — changes are saved locally and will sync when you&apos;re back online
                    </div>
                )}

                <TopBar
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    onOpenQuickOpen={() => setShowQuickOpen(true)}
                    onOpenSearch={() => setShowSearch(true)}
                    onOpenShare={() => setShowShare(true)}
                    onOpenHistory={handleOpenWorkspaceHistory}
                    onOpenPicker={() => setShowPicker(true)}
                    onOpenProfile={handleOpenProfile}
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                    onToggleDrawer={() => setDrawerOpen(!drawerOpen)}
                    drawerOpen={drawerOpen}
                    avatarEmoji={identity?.emoji || '👤'}
                    isGuest={!hasIdentity}
                    onOpenSettings={() => setShowSettings(true)}
                    onPaste={handleTopBarPaste}
                    onOpenPath={(path) => handleSelectFile(path)}
                />

                <div className="app-body">
                    {/* Backdrop for mobile drawer */}
                    {drawerOpen && (
                        <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
                    )}

                    {/* Sidebar / Drawer */}
                    <aside
                        className={`sidebar ${drawerOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''} ${isResizing ? 'resizing' : ''}`}
                        style={!sidebarCollapsed ? { width: sidebarWidth } : undefined}
                    >
                        <FileBrowser
                            selectedPath={activePath}
                            onSelect={handleSelectFile}
                            onOpenHistory={handleOpenFileHistory}
                            onOpenComponentJob={handleOpenComponentJob}
                            onPathsRenamed={renameTabPaths}
                            onPathsDeleted={removeTabPaths}
                            canWrite={canWrite}
                        />
                    </aside>

                    {/* Resize handle (desktop only) */}
                    {!sidebarCollapsed && (
                        <div
                            className="resize-handle"
                            role="separator"
                            aria-orientation="vertical"
                            aria-valuenow={sidebarWidth}
                            aria-valuemin={SIDEBAR_MIN}
                            aria-valuemax={SIDEBAR_MAX}
                            aria-label="Resize sidebar"
                            tabIndex={0}
                            onMouseDown={handleResizeStart}
                            onDoubleClick={handleResizeDoubleClick}
                            title="Drag to resize, double-click to reset"
                        />
                    )}

                    {/* Main content area */}
                    <main className={`main-content ${switchTransition ? 'workspace-switching' : ''}`}>
                        {switchTransition && <div className="workspace-skeleton" aria-hidden="true" />}
                        <Tabs
                            openPaths={openPaths}
                            activePath={activePath}
                            onSelect={handleSelectFile}
                            onClose={handleCloseTab}
                            onReorder={handleReorderTabs}
                            onContextMenu={handleTabContextMenu}
                            externallyModifiedPaths={externallyModifiedPaths}
                            loadingPaths={loadingPaths}
                            canWrite={canWrite}
                            renamingPath={tabRenamingPath}
                            onRename={(oldPath, newPath) => {
                                const ops = cascadeRename(oldPath, newPath, workspace.fs.list(), renameFile)
                                // Remap ALL affected open tab paths (primary + companions + directory children)
                                renameTabPaths(ops)
                                toast(`Renamed to ${newPath.split('/').pop()}`, { type: 'success', duration: 2000 })
                            }}
                        />
                        <EditorView
                            path={activePath}
                            diffState={activePath && diffState?.path === activePath ? diffState : null}
                            onExitDiff={handleExitDiff}
                            onOpenFile={handleSelectFile}
                            onLoadingChange={handleLoadingChange}
                            initialViewMode={initialViewMode}
                            requestedViewMode={requestedEditorMode?.path === activePath ? requestedEditorMode.mode : null}
                            onRequestedViewModeApplied={() => setRequestedEditorMode(current => current?.path === activePath ? null : current)}
                            onViewModeChange={handleViewModeChange}
                            scrollToLine={scrollToLine}
                            onScrollToLineApplied={handleScrollToLineApplied}
                        />
                    </main>
                </div>

                {/* Tab context menu */}
                {tabContextMenu && (
                    <ContextMenu
                        x={tabContextMenu.x}
                        y={tabContextMenu.y}
                        targetType="file"
                        targetPath={tabContextMenu.path}
                        items={[
                            { icon: <X size={14} />, label: 'Close', action: 'close' },
                            { icon: <XCircle size={14} />, label: 'Close Others', action: 'closeOthers' },
                            { icon: <ArrowRightToLine size={14} />, label: 'Close to the Right', action: 'closeToRight' },
                            { icon: <Layers size={14} />, label: 'Close All', action: 'closeAll' },
                            'separator',
                            ...(canWrite ? [{ icon: <Pencil size={14} />, label: 'Rename', action: 'rename' }] : []),
                            { icon: <Copy size={14} />, label: 'Copy Path', action: 'copyPath' },
                            { icon: <FolderOpen size={14} />, label: 'Reveal in Sidebar', action: 'revealInSidebar' },
                        ]}
                        onAction={(action) => handleTabContextAction(action)}
                        onClose={() => setTabContextMenu(null)}
                    />
                )}

                {/* Modals */}
                {showQuickOpen && (
                    <QuickOpen
                        recentPaths={recentPaths}
                        onSelect={handleSelectFile}
                        onClose={() => setShowQuickOpen(false)}
                    />
                )}
                {showSearch && (
                    <SearchPanel
                        onSelect={handleSelectFile}
                        onClose={() => setShowSearch(false)}
                    />
                )}
                <WorkspacePicker
                    isOpen={showPicker}
                    onClose={() => setShowPicker(false)}
                    onOpenProfile={handleOpenProfile}
                />
                <ProfileSettings
                    isOpen={showProfile}
                    onClose={() => { setShowProfile(false); setProfileInitialTab(undefined) }}
                    initialTab={profileInitialTab}
                    signupEnabled={SIGNUP_ENABLED}
                />
                <ShareModal
                    isOpen={showShare}
                    onClose={() => setShowShare(false)}
                    currentFilePath={activePath}
                    currentViewMode={currentViewMode === 'agent' || currentViewMode === 'job' ? null : currentViewMode}
                />
                <HistoryPanel
                    isOpen={showHistory}
                    onClose={() => setShowHistory(false)}
                    onDiffFile={handleDiffFile}
                    onBrowseSnapshot={handleBrowseSnapshot}
                    filePath={historyPanelState.filePath}
                    initialMode={historyPanelState.initialMode}
                />
                <SnapshotBrowserPanel
                    isOpen={snapshotCommit !== null}
                    commit={snapshotCommit}
                    onClose={() => setSnapshotCommit(null)}
                />
                {showUnlockPrompt && (
                    <UnlockPrompt
                        variant="local"
                        onUnlock={unlockWithWebAuthn}
                        onDismiss={() => setShowUnlockPrompt(false)}
                    />
                )}
                {showPwaSignIn && (
                    <UnlockPrompt
                        variant="pwa"
                        dismissable={!isStandalone}
                        onUnlock={unlockWithWebAuthn}
                        onDismiss={() => { setPwaSignInDismissed(true); sessionStorage.setItem('sb:pwa-sign-in-dismissed', '1'); setShowPwaSignIn(false) }}
                        online={online}
                        signupEnabled={SIGNUP_ENABLED}
                        onRecoverFromServer={async () => {
                            const ok = await recoverFromServer()
                            if (ok) {
                                toast('Profile restored ✓', { type: 'success' })
                                setShowPwaSignIn(false)
                            }
                            return ok
                        }}
                        onImport={() => {
                            setShowPwaSignIn(false)
                            setProfileInitialTab('identity')
                            setShowProfile(true)
                        }}
                        onCreate={SIGNUP_ENABLED ? async () => {
                            setShowPwaSignIn(false)
                            try {
                                const id = await createIdentity({ webAuthnOnly: true })
                                if (!id) return
                                toast('Profile created — save your backup key!', { type: 'success', duration: 5000 })
                                setProfileInitialTab('export')
                                setShowProfile(true)
                            } catch (err) {
                                toast(err instanceof Error ? err.message : 'Failed to create profile', { type: 'error' })
                            }
                        } : undefined}
                    />
                )}
                {isGuest && !showPwaSignIn && (
                    <GuestBanner
                        signupEnabled={SIGNUP_ENABLED}
                        onCreateProfile={SIGNUP_ENABLED ? async () => {
                            try {
                                const id = await createIdentity({ webAuthnOnly: true })
                                if (!id) return // User cancelled WebAuthn — stay as guest
                                toast('Profile created — save your backup key!', { type: 'success', duration: 5000 })
                                // Open ProfileSettings on export tab so user sees backup key reminder (spec §5.2)
                                setProfileInitialTab('export')
                                setShowProfile(true)
                            } catch (err) {
                                toast(err instanceof Error ? err.message : 'Failed to create profile', { type: 'error' })
                            }
                        } : undefined}
                        onRecoverFromServer={async () => {
                            const ok = await recoverFromServer()
                            if (ok) {
                                toast('Profile restored ✓', { type: 'success' })
                            } else {
                                toast('No existing profile found on this device\'s keychain', { type: 'info' })
                            }
                            return ok
                        }}
                    />
                )}

                <WorkspaceSettings
                    isOpen={showSettings}
                    onClose={() => setShowSettings(false)}
                    onOpenPicker={() => setShowPicker(true)}
                    onSelectFile={handleSelectFile}
                    onOpenNotificationDevices={() => {
                        setShowSettings(false)
                        setProfileInitialTab('notifications')
                        setShowProfile(true)
                    }}
                />

                {deletedOverlay && (
                    <WorkspaceDeletedOverlay
                        label={deletedOverlay.label}
                        deletedByName={deletedOverlay.deletedByName}
                        onOpenPicker={() => { setDeletedOverlay(null); setShowPicker(true) }}
                    />
                )}

                <div className="floating-widget-stack">
                    <ComponentJobMiniWidget currentPath={activePath} onOpenFile={handleSelectFile} />

                    {/* Embedded agent floating mini-widget — persists across all views */}
                    <AgentMiniWidget onOpenAgent={handleSelectFile} />
                </div>

                {/* PWA Install Banner */}
                <InstallBanner />
            </div>
        </EmbeddedAgentProvider>
    )
}

export default function App() {
    const route = getDedicatedAppPage(window.location.pathname, APP_BASE_PATH)

    if (route === 'changes') {
        return <ChangesPage />
    }

    return (
        <WorkspaceProvider>
            <ToastProvider>
                {route === 'admin' ? <AdminPage /> : route === 'debug' ? <DebugPage /> : route === 'mcp-authorize' ? <McpAuthorizePage /> : <AppShell />}
            </ToastProvider>
        </WorkspaceProvider>
    )
}
